import {
  DarwinCol,
  DarwinFlex,
  DarwinRow,
  DarwinSpin,
} from 'components/darwin-ui';
import DarwinTypography from 'components/darwin-ui/Typography';
import { useTranslation } from 'react-i18next';
import {
  InfoCircleFilled,
  UnlockOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { AssignableCompany } from 'types/organizationTypes';
import { Control, FieldErrors } from 'react-hook-form';
import styles from '../../Users.module.css';
import { CreateUserFormData, CreateUserFormInputProps } from 'types/userTypes';
import { InputType } from 'types/genericTypes';
import { USER_EMAIL_MAX_LENGTH } from 'utils/users';
import { GenericInput } from 'components/ui/GenericInput';

const { Title } = DarwinTypography;

interface ValidateUserSectionProps {
  control: Control<CreateUserFormData>;
  errors: FieldErrors<CreateUserFormData>;
  assignableOrganizations: AssignableCompany[];
  isFetching: boolean;
  isViewForm: boolean;
}

export const ValidateUserSection = ({
  control,
  errors,
  assignableOrganizations,
  isFetching,
  isViewForm,
}: ValidateUserSectionProps) => {
  const { t } = useTranslation();

  const firstColumnInputs: CreateUserFormInputProps = [
    {
      inputType: InputType.INPUT,
      fieldName: 'email',
      label: 'Email',
      showRequiredAsterisk: true,
      inputRules: {
        required: t('create-user-form.email-required'),
        maxLength: USER_EMAIL_MAX_LENGTH,
        pattern: {
          value: /^\S+@\S+$/i,
          message: t('create-user-form.email-format'),
        },
      },
      maxLength: USER_EMAIL_MAX_LENGTH,
      disabled: isViewForm,
    },
  ];

  const secondColumnInputs: CreateUserFormInputProps = [
    {
      inputType: InputType.DROPDOWN,
      fieldName: 'organization',
      label: t('create-user-form.single-organization'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('create-user-form.organization-required'),
      },
      dropdownData:
        assignableOrganizations?.map(org => ({
          ...org,
          label: org.name,
        })) || [],
      disabled: isViewForm,
    },
  ];
  return (
    <>
      {/* User Validation */}
      <Title level={5} className={styles.userFormRowTitle}>
        <UnlockOutlined /> {t('create-user-form.user-validation')}
      </Title>
      {!isViewForm && (
        <DarwinRow className={styles.userFormValidationUserInfo} gutter={16}>
          <DarwinCol span={24}>
            <InfoCircleFilled
              className={styles.userFormValidateEmailOrganizationMessageIcon}
            />
            {t('create-user-form.validation-user-info-message')}
          </DarwinCol>
        </DarwinRow>
      )}
      {/* Email and Organization */}
      <DarwinRow
        style={{
          borderTopRightRadius: !isViewForm ? '0px' : '',
          borderTopLeftRadius: !isViewForm ? '0px' : '',
        }}
        className={styles.userFormRow}
        gutter={16}
      >
        <DarwinCol span={12}>
          {firstColumnInputs.map(input => (
            <GenericInput
              key={input.fieldName}
              control={control}
              errors={errors}
              {...input}
            />
          ))}
        </DarwinCol>

        <DarwinCol span={11}>
          {secondColumnInputs.map(input => (
            <GenericInput
              key={input.fieldName}
              control={control}
              errors={errors}
              {...input}
            />
          ))}
        </DarwinCol>

        {isFetching && (
          <DarwinFlex vertical justify="center" align="center">
            <DarwinSpin indicator={<LoadingOutlined spin />} />
          </DarwinFlex>
        )}
      </DarwinRow>
    </>
  );
};
